import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ApiSidebar = ({ activeSection, onSectionChange, isMobileMenuOpen, onMobileMenuToggle }) => {
  const [expandedSections, setExpandedSections] = useState({
    'getting-started': true,
    'authentication': false,
    'messaging': false,
    'webhooks': false,
    'sdks': false
  });

  const apiSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: 'Rocket',
      items: [
        { id: 'overview', title: 'Overview' },
        { id: 'quickstart', title: 'Quick Start' },
        { id: 'rate-limits', title: 'Rate Limits' }
      ]
    },
    {
      id: 'authentication',
      title: 'Authentication',
      icon: 'Shield',
      items: [
        { id: 'api-keys', title: 'API Keys' },
        { id: 'oauth', title: 'OAuth 2.0' },
        { id: 'security', title: 'Security Best Practices' }
      ]
    },
    {
      id: 'messaging',
      title: 'Messaging API',
      icon: 'MessageSquare',
      items: [
        { id: 'send-message', title: 'Send Message' },
        { id: 'get-messages', title: 'Get Messages' },
        { id: 'message-status', title: 'Message Status' },
        { id: 'bulk-messaging', title: 'Bulk Messaging' }
      ]
    },
    {
      id: 'webhooks',
      title: 'Webhooks',
      icon: 'Webhook',
      items: [
        { id: 'webhook-setup', title: 'Setup Webhooks' },
        { id: 'webhook-events', title: 'Event Types' },
        { id: 'webhook-security', title: 'Webhook Security' }
      ]
    },
    {
      id: 'sdks',
      title: 'SDKs & Libraries',
      icon: 'Code',
      items: [
        { id: 'javascript-sdk', title: 'JavaScript SDK' },
        { id: 'python-sdk', title: 'Python SDK' },
        { id: 'php-sdk', title: 'PHP SDK' },
        { id: 'java-sdk', title: 'Java SDK' },
        { id: 'csharp-sdk', title: 'C# SDK' }
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const handleItemClick = (itemId) => {
    onSectionChange(itemId);
    if (window.innerWidth < 1024) {
      onMobileMenuToggle();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden"
          onClick={onMobileMenuToggle}
        />
      )}
      {/* Sidebar */}
      <div className={`
        fixed lg:sticky top-16 left-0 h-[calc(100vh-4rem)] w-80 bg-card border-r border-border
        transform transition-transform duration-300 ease-in-out z-50 lg:z-0
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        overflow-y-auto
      `}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-foreground">API Reference</h2>
            <button
              onClick={onMobileMenuToggle}
              className="lg:hidden p-2 hover:bg-muted rounded-md transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <nav className="space-y-2">
            {apiSections?.map((section) => (
              <div key={section?.id} className="space-y-1">
                <button
                  onClick={() => toggleSection(section?.id)}
                  className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <Icon name={section?.icon} size={16} />
                    <span>{section?.title}</span>
                  </div>
                  <Icon 
                    name="ChevronDown" 
                    size={16} 
                    className={`transform transition-transform ${
                      expandedSections?.[section?.id] ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {expandedSections?.[section?.id] && (
                  <div className="ml-6 space-y-1">
                    {section?.items?.map((item) => (
                      <button
                        key={item?.id}
                        onClick={() => handleItemClick(item?.id)}
                        className={`block w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                          activeSection === item?.id
                            ? 'text-primary bg-primary/10 border-l-2 border-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }`}
                      >
                        {item?.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="mt-8 p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="HelpCircle" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">Need Help?</span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Check our support resources or contact our developer team.
            </p>
            <div className="space-y-2">
              <a
                href="/contact-sales"
                className="block text-xs text-primary hover:text-primary/80 transition-colors"
              >
                Contact Support →
              </a>
              <a
                href="/developer-portal"
                className="block text-xs text-primary hover:text-primary/80 transition-colors"
              >
                Developer Portal →
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ApiSidebar;